import { SchulteRoiCalculator } from "./roi-calculator.js";
import { layoutFromScriptUrl } from "./layouts.js";

const scriptLayout = layoutFromScriptUrl(import.meta.url);

export const TAG_NAME = "schulte-roi-calculator";

/** Marks a plain element the script replaces with the calculator. */
export const PLACEHOLDER_SELECTOR = "[data-schulte-roi]";

if (typeof customElements !== "undefined" && !customElements.get(TAG_NAME)) {
  customElements.define(TAG_NAME, SchulteRoiCalculator);
}

/**
 * Attribute names the placeholder may carry as data-* attributes, mapped to the element's attributes.
 * data-* survives CMS sanitizers (Contao keeps id, class, style, title and data-* only).
 */
const PLACEHOLDER_ATTRIBUTES: ReadonlyArray<readonly [dataKey: string, attribute: string]> = [
  ["layout", "layout"],
  ["apiUrl", "api-url"],
  ["configUrl", "config-url"],
  ["privacyUrl", "privacy-url"],
  ["privacyNoticeVersion", "privacy-notice-version"],
  ["consentCheckbox", "consent-checkbox"],
];

/**
 * Mounts a calculator into every `[data-schulte-roi]` placeholder that has none yet and returns the
 * created elements. Runs once automatically when the script loads; call it again for content that
 * arrives later.
 *
 *   <div data-schulte-roi data-privacy-url="https://…/datenschutz"></div>
 *
 * api-url defaults to /v1/leads on the origin this script was loaded from, so it can be left out.
 * data-privacy-notice-version is optional; leads without it are stored as "unversioned".
 */
export function mountPlaceholders(root: ParentNode = document): SchulteRoiCalculator[] {
  const created: SchulteRoiCalculator[] = [];
  for (const placeholder of root.querySelectorAll<HTMLElement>(PLACEHOLDER_SELECTOR)) {
    if (placeholder.querySelector(TAG_NAME) || placeholder.tagName.toLowerCase() === TAG_NAME) continue;
    const element = document.createElement(TAG_NAME) as SchulteRoiCalculator;
    // Explicit data-layout wins, including data-layout="classic" on a variant script.
    element.setAttribute("layout", scriptLayout);
    for (const [dataKey, attribute] of PLACEHOLDER_ATTRIBUTES) {
      const value = placeholder.dataset[dataKey];
      if (value !== undefined) element.setAttribute(attribute, value);
    }
    placeholder.append(element);
    created.push(element);
  }
  return created;
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mountPlaceholders(), { once: true });
  } else {
    mountPlaceholders();
  }
}

export { SchulteRoiCalculator };
export { buildLeadSubmission, submitLead } from "./submission.js";

declare global {
  interface HTMLElementTagNameMap {
    "schulte-roi-calculator": SchulteRoiCalculator;
  }
}
